/* eslint-disable */

import {useEffect, useState, useCallback, useRef} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {BreadCrumb} from "primereact/breadcrumb";
import {toast} from "react-toastify";
import axios from "axios";
import "moment/locale/uz";
import MainLayout from "../components/MainLayout";

import {
	getAllStorage,
	getDrivers,
	getMagazins,
	getSborshiks,
} from "../services/shipment.service";
import StoreSubmitSection from "../components/shipment/store/submitSection";
import StoreTableSection from "../components/shipment/store/tableSection";
import StoreHeaderSection from "../components/shipment/store/headerSection";
import { getBlocksOfStorage } from "../services/block.service";

const ReleaseStorePage = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const API_BASE = process.env.REACT_APP_BASE_URL;

	const isEditMode = !!location.state?.editedData;
	const editData = location.state?.editedData;

	// States
	const [furnitureItems, setFurnitureItems] = useState([]);
	const [filteredItems, setFilteredItems] = useState([]);
	const [storageBlocks, setStorageBlocks] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [selectedItems, setSelectedItems] = useState([]);
	const [selectedOrder, setSelectedOrder] = useState(null);
	const [filteredOrders, setFilteredOrders] = useState([]);
	const [selectedViewOrder, setSelectedViewOrder] = useState(null);
	const [shipmentDate, setShipmentDate] = useState(new Date());
	const [selectedDrivers, setSelectedDrivers] = useState(null);
	const [selectedCollectors, setSelectedCollectors] = useState([]);
	const [selectedMagazin, setSelectedMagazin] = useState("");
	const [shipmentComment, setShipmentComment] = useState("");
	const [availableOrders, setAvailableOrders] = useState([]);
	const [availableDrivers, setAvailableDrivers] = useState([]);
	const [availableCollectors, setAvailableCollectors] = useState([]);
	const [availableMagazins, setAvailableMagazins] = useState([]);
	const [availableUniqueNames, setAvailableUniqueNames] = useState([]);
	const [selectedSerialAmount, setSelectedSerialAmount] = useState();
	const [isCreatingShipment, setIsCreatingShipment] = useState(false);
	const [itemQuantities, setItemQuantities] = useState({});

	// Refs
	const modifiedBlocksCache = useRef({});
	const [updateCounter, setUpdateCounter] = useState(0);

	// Initialize edit mode data
	useEffect(() => {
		if (isEditMode && editData && Array.isArray(editData)) {
			console.log("Edit mode data:", editData);

			const firstItem = editData[0];

			if (firstItem.shipmentInfo && firstItem.shipmentInfo.to_where) {
				setSelectedMagazin(firstItem.shipmentInfo.to_where);
			}

			const orderName = firstItem.demand_furniture?.demand?.doc_no;
			if (orderName) {
				setSelectedOrder(orderName);
				setSelectedViewOrder(orderName);
				setAvailableOrders([orderName]);
			}

			if (firstItem.shipmentInfo) {
				if (firstItem.shipmentInfo.shipment_date) {
					setShipmentDate(new Date(firstItem.shipmentInfo.shipment_date));
				}

				if (firstItem.shipmentInfo.description) {
					setShipmentComment(firstItem.shipmentInfo.description);
				}

				if (firstItem.shipmentInfo.driver_id) {
					setSelectedDrivers(firstItem.shipmentInfo.driver_id);
				}

				if (
					firstItem.shipmentInfo.sborshik_id &&
					Array.isArray(firstItem.shipmentInfo.sborshik_id)
				) {
					setSelectedCollectors(firstItem.shipmentInfo.sborshik_id);
				}
			}

			const quantities = {};
			editData.forEach((item) => {
				quantities[item.id] = item.shipment_amount || item.amount || 1;
			});
			setItemQuantities(quantities);

			setFurnitureItems(editData);
			setFilteredItems(editData);
			setSelectedItems(editData);
		}
	}, [isEditMode, editData]);

	// Data Fetching
	const fetchFurnitureData = useCallback(async () => {
		if (isEditMode) return;

		setIsLoading(true);
		try {
			const {uniques} = await getAllStorage();

			const filteredItems = uniques.filter(
				(value) => !value.demand_furniture_id
			);

			setFurnitureItems(filteredItems);
			setFilteredItems(filteredItems);

			const allOrders = filteredItems
				.map((item) => item.demand_furniture?.demand?.doc_no)
				.filter(Boolean);

			const allUniqueNames = filteredItems.map((item) => item.unique.name);

			setAvailableUniqueNames(allUniqueNames);
			setAvailableOrders(Array.from(new Set(allOrders)));
		} catch (error) {
			console.error("Failed to fetch furniture data:", error);
			toast.error("Маълумотларни юклашда хатолик юз берди");
		} finally {
			setIsLoading(false);
		}
	}, [API_BASE, isEditMode]);

	const fetchDriversAndCollectors = async () => {
		try {
			const [driversRes, collectorsRes, magazinRes] = await Promise.all([
				getDrivers(),
				getSborshiks(),
				getMagazins(),
			]);

			setAvailableDrivers(driversRes);
			setAvailableCollectors(collectorsRes);
			setAvailableMagazins(magazinRes);
		} catch (error) {
			console.error("Failed to fetch drivers and collectors:", error);
			toast.error("Шафёр ва йиғувчилар маълумотларини юклашда хатолик");
		}
	};

	const fetchStorageBlocks = useCallback(async () => {
		try {
			const response = await getBlocksOfStorage();
			setStorageBlocks(response);
		} catch (error) {
			console.error("Failed to fetch storage blocks:", error);
			toast.error("Склад маълумотларини юклашда хатолик");
		}
	}, [API_BASE]);

	// Effects
	useEffect(() => {
		fetchFurnitureData();
		fetchDriversAndCollectors();
		fetchStorageBlocks();
	}, [fetchFurnitureData, fetchStorageBlocks]);

	// Filter items based on selected order
	useEffect(() => {
		if (isEditMode) return;

		if (!selectedOrder) {
			setFilteredItems(furnitureItems);
			setSelectedItems(furnitureItems);
			return;
		}

		let filtered = [];
		if (/[a-zA-Z]+[0-9]+$/.test(selectedOrder)) {
			filtered = furnitureItems.filter((item) => {
				return item.unique.name === selectedOrder;
			});
		} else {
			filtered = furnitureItems.filter((item) => {
				return item.demand_furniture?.demand?.doc_no === selectedOrder;
			});
		}

		setFilteredItems(filtered);
		setSelectedItems(filtered);
	}, [selectedOrder, furnitureItems, isEditMode]);

	// Handlers
	const searchOrders = (e) => {
		const query = e.query.toLowerCase();
		let filtered = [];
		if (/[a-zA-Z]+[0-9]+$/.test(query)) {
			filtered = availableUniqueNames.filter((order) =>
				order.toLowerCase().includes(query)
			);
		} else {
			filtered = availableOrders.filter((order) =>
				order.toLowerCase().includes(query)
			);
		}
		setFilteredOrders(filtered);
	};

	const handleQuantityChange = (itemId, value) => {
		setItemQuantities((prev) => ({
			...prev,
			[itemId]: value,
		}));
	};

	const handleCreateShipment = async () => {
		setIsCreatingShipment(true);
		try {
			if (!shipmentDate) {
				toast.error("Санани танланг");
				return;
			}

			if (!selectedItems.length) {
				toast.error("Камида битта буюмни танланг");
				return;
			}

			if (!selectedMagazin) {
				toast.error("Магазинни танланг");
				return;
			}

			const selectedItemIds = selectedItems.map((item) => item.id);

			const selectedItemsIds = selectedItems.map((item) => {
				return {
					id: item.id,
					amount: item.amount,
				};
			});

			const localDate = new Date(
				shipmentDate.getFullYear(),
				shipmentDate.getMonth(),
				shipmentDate.getDate(),
				5,
				0,
				0
			);

			const date = new Date(localDate);
			const formatted = date.toISOString().slice(0, 10);

			const shipmentPayload = {
				date: formatted,
				description: shipmentComment,
				driver: {id: selectedDrivers},
				sborshiks: selectedCollectors.map((collector) => {
					return {id: collector};
				}),
				demand_furniture: selectedItemsIds,
			};

			const editShipmentPayload = {
				driver_id: selectedDrivers,
				sborshik_id: selectedCollectors,
				from_where: `SKLAD`,
				to_where: selectedMagazin,
				description: shipmentComment,
				shipment_date: localDate.toISOString(),
				serialIds: selectedItemIds,
			};

			console.log(selectedItems);
			console.log(editShipmentPayload);
			console.log("Item quantities:", itemQuantities);

			if (isEditMode) {
				await axios.put(
					`https://677adc96671ca030683503e2.mockapi.io/products/shipments/${editData[0].shipment_id}`,
					editShipmentPayload
				);

				toast.success("Жўнатма муваффақиятли ўзгартирилди");
				navigate(-1);
			} else {
				const shipmentResponse = await axios.post(
					`${process.env.REACT_APP_SECOND_URL}/api/v1/gotoviy/shipments`,
					shipmentPayload
				);

				console.log(shipmentResponse.data);

				setFurnitureItems((prev) =>
					prev.filter((item) => !selectedItemIds.includes(item.id))
				);
				setFilteredItems((prev) =>
					prev.filter((item) => !selectedItemIds.includes(item.id))
				);

				toast.success("Жўнатма муваффақиятли яратилди");
				navigate(`/release`);
				resetForm();
			}
		} catch (error) {
			console.error("Shipment creation/update error:", error);
			toast.error(
				isEditMode
					? "Жўнатма ўзгартиришда хатолик юз берди"
					: "Жўнатма яратишда хатолик юз берди"
			);
		} finally {
			setIsCreatingShipment(false);
		}
	};

	const resetForm = () => {
		setSelectedItems([]);
		setSelectedOrder(null);
		setSelectedViewOrder(null);
		setSelectedDrivers(null);
		setSelectedCollectors([]);
		setShipmentComment("");
		setShipmentDate(new Date());
		setItemQuantities({});
	};

	// Breadcrumb items
	const breadcrumbItems = [
		{
			label: "Отгрузкалар",
			template: () => (
				<span className="cursor-pointer" onClick={() => navigate(`/release`)}>
					Отгрузкалар
				</span>
			),
		},
		{
			label: isEditMode ? "Отгрузкани ўзгартириш" : "Магазинга отгрузка қилиш",
			template: () => (
				<span className="font-semibold text-primary">
					{isEditMode ? "Отгрузкани ўзгартириш" : "Магазинга отгрузка қилиш"}
				</span>
			),
		},
	];

	return (
		<MainLayout
			header={
				isEditMode ? `ОТГРУЗКАНИ ЎЗГАРТИРИШ` : `МАГАЗИНГА ОТГРУЗКА ҚИЛИШ`
			}>
			<div className="mr-1">
				{location.state?.breadCrumb && (
					<BreadCrumb model={breadcrumbItems} className="px-0 pt-0 mb-6" />
				)}

				<div className="flex justify-between mb-4">
					<StoreHeaderSection
						selectedViewOrder={selectedViewOrder}
						setSelectedViewOrder={setSelectedViewOrder}
						setSelectedOrder={setSelectedOrder}
						filteredOrders={filteredOrders}
						searchOrders={searchOrders}
						shipmentDate={shipmentDate}
						setShipmentDate={setShipmentDate}
						isEditMode={isEditMode}
					/>
				</div>

				{(selectedOrder || isEditMode) && (
					<StoreTableSection
						filteredItems={filteredItems}
						isLoading={isLoading}
						updateCounter={updateCounter}
						selectedSerialAmount={selectedSerialAmount}
						setSelectedSerialAmount={setSelectedSerialAmount}
					/>
				)}

				{(selectedOrder || isEditMode) && (
					<StoreSubmitSection
						availableMagazins={availableMagazins}
						selectedMagazin={selectedMagazin}
						setSelectedMagazin={setSelectedMagazin}
						availableDrivers={availableDrivers}
						selectedDrivers={selectedDrivers}
						setSelectedDrivers={setSelectedDrivers}
						availableCollectors={availableCollectors}
						selectedCollectors={selectedCollectors}
						setSelectedCollectors={setSelectedCollectors}
						shipmentComment={shipmentComment}
						setShipmentComment={setShipmentComment}
						isEditMode={isEditMode}
						isCreatingShipment={isCreatingShipment}
						isLoading={isLoading}
						handleCreateShipment={handleCreateShipment}
					/>
				)}
			</div>
		</MainLayout>
	);
};

export default ReleaseStorePage;
